import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Newspaper, Sparkles, AlertTriangle, Users as UsersIcon, FileWarning,
  Trash2, ExternalLink, Loader2, ShieldAlert, TrendingUp, FileText, X
} from "lucide-react";
import { toast } from "sonner";

const API = `${window.location.origin}/api/adverse-media`;

const SEVERITY_COLORS = {
  low:      { bg: "rgba(34, 197, 94, 0.12)",  border: "#22c55e", text: "#22c55e" },
  medium:   { bg: "rgba(245, 158, 11, 0.12)", border: "#f59e0b", text: "#f59e0b" },
  high:     { bg: "rgba(239, 68, 68, 0.15)",  border: "#ef4444", text: "#ef4444" },
  critical: { bg: "rgba(220, 38, 38, 0.20)",  border: "#dc2626", text: "#fca5a5" },
};

const RISK_CATEGORY_LABELS = {
  money_laundering: "Money Laundering",
  fraud: "Fraud",
  bribery: "Bribery & Corruption",
  sanctions: "Sanctions",
  terror_financing: "Terror Financing",
  cybercrime: "Cybercrime",
  tax_evasion: "Tax Evasion",
  other: "Other",
};

function SeverityBadge({ level, score }) {
  const c = SEVERITY_COLORS[level] || SEVERITY_COLORS.low;
  return (
    <span
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.text,
        borderRadius: "6px",
        padding: "4px 10px",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.4px",
        textTransform: "uppercase",
      }}
      data-testid={`severity-badge-${level}`}
    >
      {level} · {score}
    </span>
  );
}

function ExtractionResults({ extraction, matchedCustomers, onSave, saving }) {
  const sev = SEVERITY_COLORS[extraction?.severity_level] || SEVERITY_COLORS.low;
  return (
    <div
      style={{
        background: "#0d1117",
        border: `1px solid ${sev.border}`,
        borderRadius: "12px",
        padding: "24px",
        marginTop: "20px",
      }}
      data-testid="extraction-results"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Sparkles style={{ color: sev.text, width: 22, height: 22 }} />
          <h3 style={{ color: "#f1f5f9", fontSize: "18px", fontWeight: 700, margin: 0 }}>
            AI Risk Extraction
          </h3>
          <SeverityBadge level={extraction.severity_level} score={extraction.severity_score} />
        </div>
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="save-extraction-btn"
          style={{
            background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
            border: "none",
            borderRadius: "8px",
            color: "white",
            padding: "10px 18px",
            fontWeight: 600,
            fontSize: "13px",
            cursor: saving ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {saving ? <Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> : <FileText style={{ width: 14, height: 14 }} />}
          {saving ? "Saving..." : "Save & Auto-link"}
        </button>
      </div>

      {/* Summary */}
      {extraction.summary && (
        <div style={{ color: "#cbd5e1", fontSize: "14px", marginBottom: "20px", lineHeight: 1.6 }}>
          {extraction.summary}
        </div>
      )}

      {/* Grid: Names | Entities | Metadata */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px", marginBottom: "20px" }}>
        <div>
          <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
            Names Extracted ({extraction.names?.length || 0})
          </div>
          {extraction.names?.length ? extraction.names.map((n, idx) => (
            <div key={idx} style={{ background: "#080c12", borderRadius: "6px", padding: "8px 12px", marginBottom: "6px" }}>
              <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{n.name}</div>
              {n.aliases?.length > 0 && <div style={{ color: "#94a3b8", fontSize: "11px" }}>aka {n.aliases.join(", ")}</div>}
              {n.role && <div style={{ color: "#64748b", fontSize: "10px", textTransform: "uppercase" }}>{n.role}</div>}
            </div>
          )) : <div style={{ color: "#64748b", fontSize: "12px" }}>None detected</div>}
        </div>

        <div>
          <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
            Entities ({extraction.entities?.length || 0})
          </div>
          {extraction.entities?.length ? extraction.entities.map((e, idx) => (
            <div key={idx} style={{ background: "#080c12", borderRadius: "6px", padding: "8px 12px", marginBottom: "6px" }}>
              <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{e.name}</div>
              <div style={{ color: "#64748b", fontSize: "10px", textTransform: "uppercase" }}>{e.type}</div>
            </div>
          )) : <div style={{ color: "#64748b", fontSize: "12px" }}>None detected</div>}
        </div>

        <div>
          <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
            Metadata
          </div>
          <div style={{ background: "#080c12", borderRadius: "6px", padding: "10px 12px", fontSize: "12px", lineHeight: 1.8 }}>
            <div><span style={{ color: "#64748b" }}>Category:</span> <span style={{ color: "#f1f5f9", fontWeight: 600 }}>{RISK_CATEGORY_LABELS[extraction.risk_category] || extraction.risk_category}</span></div>
            <div><span style={{ color: "#64748b" }}>Sentiment:</span> <span style={{ color: "#f1f5f9" }}>{extraction.sentiment}</span></div>
            <div><span style={{ color: "#64748b" }}>Jurisdiction:</span> <span style={{ color: "#f1f5f9" }}>{extraction.jurisdiction || "—"}</span></div>
            <div><span style={{ color: "#64748b" }}>Incident:</span> <span style={{ color: "#f1f5f9" }}>{extraction.incident_date || "—"}</span></div>
            <div><span style={{ color: "#64748b" }}>Source:</span> <span style={{ color: "#f1f5f9" }}>{extraction.source_credibility}</span></div>
          </div>
        </div>
      </div>

      {/* Allegations */}
      {extraction.allegations?.length > 0 && (
        <div style={{ marginBottom: "20px" }}>
          <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
            Allegations
          </div>
          {extraction.allegations.map((a, idx) => (
            <div key={idx} style={{ background: "rgba(239, 68, 68, 0.08)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "6px", padding: "8px 12px", marginBottom: "6px", color: "#fca5a5", fontSize: "13px" }}>
              {a}
            </div>
          ))}
        </div>
      )}

      {/* Matched customers */}
      <div style={{ borderTop: "1px solid #1e2530", paddingTop: "16px" }}>
        <div className="flex items-center gap-2 mb-3">
          <UsersIcon style={{ width: 16, height: 16, color: matchedCustomers?.length ? "#ef4444" : "#64748b" }} />
          <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 700 }}>
            Matched Customers ({matchedCustomers?.length || 0})
          </div>
        </div>
        {matchedCustomers?.length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
            {matchedCustomers.map((mc, idx) => (
              <div key={idx} style={{ background: "rgba(239, 68, 68, 0.10)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "8px", padding: "12px" }}>
                <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 700 }}>{mc.customer_name}</div>
                <div style={{ color: "#94a3b8", fontSize: "11px" }}>Extracted: {mc.extracted_name}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span style={{ color: "#ef4444", fontSize: "11px", fontWeight: 600 }}>{mc.customer_risk_level} · {mc.customer_risk_score}/100</span>
                  <a href={`/customers/${mc.customer_id}`} style={{ color: "#2563eb", fontSize: "11px", textDecoration: "none" }}>View →</a>
                </div>
              </div>
            ))}
            {(extraction.severity_level === "high" || extraction.severity_level === "critical") && (
              <div style={{ gridColumn: "1 / -1", background: "rgba(245, 158, 11, 0.10)", border: "1px solid rgba(245, 158, 11, 0.3)", borderRadius: "8px", padding: "10px 14px", color: "#f59e0b", fontSize: "12px", display: "flex", alignItems: "center", gap: "8px" }}>
                <AlertTriangle style={{ width: 14, height: 14 }} />
                <strong>{extraction.severity_level.toUpperCase()}</strong> severity — Cases will be auto-created for matched customers on save.
              </div>
            )}
          </div>
        ) : (
          <div style={{ color: "#64748b", fontSize: "12px" }}>No existing customers match — saving will store extraction only.</div>
        )}
      </div>

      <div style={{ marginTop: "16px", color: "#475569", fontSize: "10px", textAlign: "right" }}>
        AI: {extraction.ai_mode || "claude-sonnet-4.5"}
      </div>
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>{label}</div>
        <Icon style={{ width: 16, height: 16, color }} />
      </div>
      <div style={{ color, fontSize: "28px", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function ArticleDetailModal({ article, onClose, onDelete }) {
  if (!article) return null;
  const ext = article.extraction || {};

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}
      data-testid="article-detail-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "28px", maxWidth: "900px", width: "100%", maxHeight: "85vh", overflowY: "auto" }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Newspaper style={{ width: 22, height: 22, color: "#2563eb" }} />
            <h3 style={{ color: "#f1f5f9", fontSize: "18px", fontWeight: 700, margin: 0 }}>
              {article.title || "Untitled article"}
            </h3>
            <SeverityBadge level={ext.severity_level} score={ext.severity_score} />
          </div>
          <button onClick={onClose} data-testid="close-modal-btn" style={{ background: "transparent", border: "none", color: "#64748b", cursor: "pointer" }}>
            <X style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {article.url && (
          <a href={article.url} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb", fontSize: "12px", display: "inline-flex", alignItems: "center", gap: "4px", marginBottom: "12px" }}>
            {article.url} <ExternalLink style={{ width: 12, height: 12 }} />
          </a>
        )}

        <div style={{ color: "#cbd5e1", fontSize: "14px", lineHeight: 1.6, marginBottom: "20px" }}>{ext.summary}</div>

        {article.created_cases?.length > 0 && (
          <div style={{ background: "rgba(239, 68, 68, 0.10)", border: "1px solid rgba(239, 68, 68, 0.3)", borderRadius: "8px", padding: "12px", marginBottom: "16px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "6px" }}>
              {article.created_cases.length} Case(s) Auto-Created
            </div>
            {article.created_cases.map((c, i) => (
              <a key={i} href={`/cases/${c.id}`} style={{ color: "#2563eb", fontSize: "13px", display: "block", marginTop: "4px", textDecoration: "none" }}>{c.case_id} → Customer #{c.customer_id?.slice(0, 8)}</a>
            ))}
          </div>
        )}

        <div style={{ background: "#080c12", borderRadius: "8px", padding: "14px", marginBottom: "16px", maxHeight: "240px", overflowY: "auto" }}>
          <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", marginBottom: "8px" }}>Original Article</div>
          <div style={{ color: "#94a3b8", fontSize: "12px", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{article.article_text}</div>
        </div>

        <div className="flex items-center justify-between" style={{ borderTop: "1px solid #1e2530", paddingTop: "12px" }}>
          <div style={{ color: "#64748b", fontSize: "11px" }}>
            Saved {new Date(article.created_at).toLocaleString()} by {article.created_by_name}
          </div>
          <button
            onClick={() => onDelete(article.id)}
            data-testid="delete-article-btn"
            style={{ background: "rgba(239, 68, 68, 0.15)", border: "1px solid #ef4444", borderRadius: "6px", color: "#ef4444", padding: "6px 12px", fontSize: "12px", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Trash2 style={{ width: 12, height: 12 }} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdverseMediaPage() {
  const [articleText, setArticleText] = useState("");
  const [articleTitle, setArticleTitle] = useState("");
  const [articleUrl, setArticleUrl] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extraction, setExtraction] = useState(null);
  const [matchedCustomers, setMatchedCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState({ total: 0, critical: 0, high: 0, cases_created: 0 });
  const [selectedArticle, setSelectedArticle] = useState(null);
  // NEW — comprehensive 4-in-1 analyze + recommended actions
  const [comprehensiveRunning, setComprehensiveRunning] = useState(false);
  const [comprehensive, setComprehensive] = useState(null);
  const [actionsRunning, setActionsRunning] = useState(false);
  const [actions, setActions] = useState(null);

  const loadArticles = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/articles`, { withCredentials: true });
      setArticles(data.articles || []);
      setStats(data.stats || { total: 0, critical: 0, high: 0, cases_created: 0 });
    } catch (e) {
      // silent — page is usable without history
    }
  }, []);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  const handleExtract = async () => {
    if (articleText.trim().length < 30) {
      toast.error("Article text must be at least 30 characters");
      return;
    }
    setExtracting(true);
    setExtraction(null);
    setMatchedCustomers([]);
    setComprehensive(null);
    setActions(null);
    try {
      const { data } = await axios.post(
        `${API}/extract`,
        { article_text: articleText, article_title: articleTitle || null, article_url: articleUrl || null },
        { withCredentials: true }
      );
      setExtraction(data.extraction);
      setMatchedCustomers(data.matched_customers || []);
      toast.success("AI extraction complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  // NEW — 4-in-1 comprehensive Claude pass (extract + network + photos + risk)
  const handleComprehensive = async () => {
    if (articleText.trim().length < 30) {
      toast.error("Article text must be at least 30 characters");
      return;
    }
    const entityFromExtraction = extraction?.names?.[0]?.name || extraction?.subject || null;
    const entity_name = (entityFromExtraction || articleTitle.split(/[—:|]/)[0].trim() || "Subject of article").slice(0, 120);
    setComprehensiveRunning(true);
    setComprehensive(null);
    setActions(null);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/adverse-media/comprehensive-analyze`,
        {
          entity_name,
          entity_type: "Person",
          article_text: articleText,
          article_title: articleTitle || null,
          article_url:   articleUrl   || null,
        },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 60_000 },
      );
      setComprehensive(data);
      toast.success(`Comprehensive analysis complete — ${data.risk_assessment?.risk_level || "—"} risk`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Comprehensive analyze failed");
    } finally {
      setComprehensiveRunning(false);
    }
  };

  // NEW — recommend next actions from a synthesised dossier
  const handleRecommendActions = async () => {
    const dossier = {
      entity_name: extraction?.names?.[0]?.name || comprehensive?.entity_network?.main_entity?.name || "Subject",
      results: {
        adverse_media: extraction || comprehensive?.adverse_media || null,
        unified_risk:  comprehensive?.risk_assessment || null,
        entity_network: comprehensive?.entity_network || null,
      },
    };
    setActionsRunning(true);
    setActions(null);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/risk/recommend-actions`,
        { dossier },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true, timeout: 30_000 },
      );
      setActions(data);
      toast.success("Next actions ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Recommend-actions failed");
    } finally {
      setActionsRunning(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/save`,
        {
          article_text: articleText,
          article_title: articleTitle || null,
          article_url: articleUrl || null,
          extraction,
        },
        { withCredentials: true }
      );
      const casesCount = data.created_cases?.length || 0;
      toast.success(casesCount > 0 ? `Saved. ${casesCount} case(s) auto-created.` : "Article saved.");
      // Reset
      setArticleText("");
      setArticleTitle("");
      setArticleUrl("");
      setExtraction(null);
      setMatchedCustomers([]);
      loadArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/articles/${id}`, { withCredentials: true });
      toast.success("Article deleted");
      setSelectedArticle(null);
      loadArticles();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="adverse-media-page" style={{ color: "#f1f5f9" }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Newspaper style={{ width: 26, height: 26, color: "#2563eb" }} />
        <h1 style={{ fontSize: "26px", fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">Adverse Media Intelligence</h1>
      </div>
      <p style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "24px" }}>
        Paste any news article — Claude AI extracts names, entities, risk category &amp; severity, then auto-links to your customers &amp; creates cases.
      </p>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
        <StatCard label="Total Articles" value={stats.total} color="#2563eb" icon={Newspaper} />
        <StatCard label="Critical" value={stats.critical} color="#fca5a5" icon={ShieldAlert} />
        <StatCard label="High" value={stats.high} color="#ef4444" icon={TrendingUp} />
        <StatCard label="Cases Auto-Created" value={stats.cases_created} color="#f59e0b" icon={FileWarning} />
      </div>

      {/* Input panel */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles style={{ width: 18, height: 18, color: "#2563eb" }} />
          <h2 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "#f1f5f9" }}>Analyse New Article</h2>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
          <input
            type="text"
            value={articleTitle}
            onChange={(e) => setArticleTitle(e.target.value)}
            placeholder="Article title (optional)"
            data-testid="article-title-input"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px" }}
          />
          <input
            type="url"
            value={articleUrl}
            onChange={(e) => setArticleUrl(e.target.value)}
            placeholder="Article URL (optional)"
            data-testid="article-url-input"
            style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px" }}
          />
        </div>

        <textarea
          value={articleText}
          onChange={(e) => setArticleText(e.target.value)}
          placeholder="Paste article body here (min 30 characters)..."
          data-testid="article-text-input"
          style={{ width: "100%", background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "12px 14px", color: "#f1f5f9", fontSize: "13px", minHeight: "180px", fontFamily: "inherit", resize: "vertical" }}
        />

        <div className="flex items-center justify-between mt-3">
          <div style={{ color: "#64748b", fontSize: "11px" }}>
            {articleText.length} characters · Powered by Claude Sonnet 4.5
          </div>
          <button
            onClick={handleExtract}
            disabled={extracting || articleText.trim().length < 30}
            data-testid="extract-btn"
            style={{
              background: extracting || articleText.trim().length < 30 ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", color: "white", padding: "10px 22px",
              fontWeight: 600, fontSize: "13px", cursor: extracting ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "8px",
              opacity: articleText.trim().length < 30 ? 0.5 : 1,
            }}
          >
            {extracting ? (
              <><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Extracting...</>
            ) : (
              <><Sparkles style={{ width: 14, height: 14 }} /> Extract Risk Signals</>
            )}
          </button>
          <button
            onClick={handleComprehensive}
            disabled={comprehensiveRunning || articleText.trim().length < 30}
            data-testid="comprehensive-analyze-btn"
            style={{
              background: comprehensiveRunning || articleText.trim().length < 30 ? "#1e2530" : "linear-gradient(135deg, #7c3aed, #6d28d9)",
              border: "none", borderRadius: "8px", color: "white", padding: "10px 22px",
              fontWeight: 600, fontSize: "13px", cursor: comprehensiveRunning ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "8px",
              opacity: articleText.trim().length < 30 ? 0.5 : 1,
              marginLeft: 8,
              boxShadow: !(comprehensiveRunning || articleText.trim().length < 30) ? "0 0 14px rgba(139,92,246,0.30)" : "none",
            }}
            title="Single Claude pass: adverse-media + entity-network + photo-recs + risk assessment in one call"
          >
            {comprehensiveRunning ? (
              <><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Analyzing 4-in-1…</>
            ) : (
              <><Sparkles style={{ width: 14, height: 14 }} /> Comprehensive Analyze</>
            )}
          </button>
        </div>
      </div>

      {/* AI extraction results */}
      {extraction && (
        <>
          <ExtractionResults
            extraction={extraction}
            matchedCustomers={matchedCustomers}
            onSave={handleSave}
            saving={saving}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
            <button
              onClick={handleRecommendActions}
              disabled={actionsRunning}
              data-testid="recommend-actions-btn"
              style={{
                background: actionsRunning ? "#1e2530" : "linear-gradient(135deg, #f59e0b, #d97706)",
                border: "none", borderRadius: 8, color: "#0b0f17", padding: "10px 18px",
                fontWeight: 700, fontSize: 13, cursor: actionsRunning ? "wait" : "pointer",
                display: "flex", alignItems: "center", gap: 8,
                boxShadow: actionsRunning ? "none" : "0 0 14px rgba(245,158,11,0.30)",
              }}
              title="Senior-AML-analyst persona suggests immediate actions / STR-SAR / workflow / senior-mgmt notes"
            >
              {actionsRunning
                ? <><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Generating Next Actions…</>
                : <>⚡ Recommend Next Actions</>}
            </button>
          </div>
        </>
      )}

      {/* NEW — Comprehensive 4-in-1 analysis results */}
      {comprehensive && (
        <div data-testid="comprehensive-results" style={{ marginTop: 18, background: "#0d1117", border: "1px solid rgba(139,92,246,0.40)", borderRadius: 12, padding: 22, boxShadow: "0 0 18px rgba(139,92,246,0.20)" }}>
          <div className="flex items-center" style={{ justifyContent: "space-between", marginBottom: 14 }}>
            <div className="flex items-center" style={{ gap: 10 }}>
              <Sparkles style={{ width: 18, height: 18, color: "#a78bfa" }} />
              <h3 style={{ color: "#e2e8f0", fontSize: 15, fontWeight: 700, margin: 0 }}>Comprehensive Analysis (4-in-1)</h3>
            </div>
            <span style={{ color: "#64748b", fontSize: 11 }}>{comprehensive.ai_mode} · {comprehensive.duration_ms}ms</span>
          </div>
          {/* Risk verdict */}
          {comprehensive.risk_assessment && (
            <div data-testid="comprehensive-risk" style={{ background: "#080c12", borderRadius: 8, padding: 14, marginBottom: 12 }}>
              <div className="flex items-center" style={{ gap: 12, marginBottom: 8 }}>
                <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Risk:</span>
                <span style={{ color: "#fca5a5", fontSize: 22, fontWeight: 800 }}>{comprehensive.risk_assessment.overall_risk_score}/100</span>
                <span style={{ background: "rgba(239,68,68,0.10)", border: "1px solid #ef4444", color: "#fca5a5", padding: "3px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>{comprehensive.risk_assessment.risk_level?.toUpperCase()}</span>
                <span style={{ color: "#64748b", fontSize: 11, marginLeft: "auto" }}>conf {comprehensive.risk_assessment.confidence_level}%</span>
              </div>
              <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.6 }}>{comprehensive.risk_assessment.executive_summary}</div>
              <div style={{ marginTop: 8 }}>
                {(comprehensive.risk_assessment.recommended_actions || []).slice(0, 6).map((a, i) => (
                  <span key={i} style={{ background: "rgba(59,130,246,0.10)", border: "1px solid rgba(59,130,246,0.35)", borderRadius: 999, padding: "3px 10px", color: "#93c5fd", fontSize: 11, marginRight: 6, marginBottom: 4, display: "inline-block" }}>{a}</span>
                ))}
              </div>
            </div>
          )}
          {/* Entity network */}
          {comprehensive.entity_network && (comprehensive.entity_network.related_entities || []).length > 0 && (
            <div data-testid="comprehensive-network" style={{ background: "#080c12", borderRadius: 8, padding: 14, marginBottom: 12 }}>
              <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Entity Network ({comprehensive.entity_network.related_entities.length})</div>
              {comprehensive.entity_network.related_entities.slice(0, 8).map((e, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px dashed rgba(148,163,184,0.10)" }}>
                  <span style={{ color: "#e2e8f0", fontSize: 13 }}>{e.name} <span style={{ color: "#64748b", fontSize: 11 }}>({e.type})</span></span>
                  <span style={{ color: "#a78bfa", fontSize: 11, fontFamily: "ui-monospace, monospace" }}>{e.relationship} · {e.confidence}%</span>
                </div>
              ))}
            </div>
          )}
          {/* Adverse-media facts */}
          {comprehensive.adverse_media && (comprehensive.adverse_media.key_facts || []).length > 0 && (
            <div data-testid="comprehensive-facts" style={{ background: "#080c12", borderRadius: 8, padding: 14 }}>
              <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Key Facts</div>
              {comprehensive.adverse_media.key_facts.slice(0, 6).map((f, i) => (
                <div key={i} style={{ display: "flex", gap: 8, padding: "4px 0", color: "#cbd5e1", fontSize: 13, lineHeight: 1.5 }}>
                  <span style={{ color: "#f59e0b" }}>•</span>{f}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* NEW — Recommend-Actions panel */}
      {actions && (
        <div data-testid="actions-results" style={{ marginTop: 18, background: "#0d1117", border: "1px solid rgba(245,158,11,0.40)", borderRadius: 12, padding: 22, boxShadow: "0 0 14px rgba(245,158,11,0.20)" }}>
          <div className="flex items-center" style={{ gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 18 }}>⚡</span>
            <h3 style={{ color: "#fbbf24", fontSize: 15, fontWeight: 700, margin: 0 }}>Recommended Next Actions</h3>
            <span style={{ color: "#64748b", fontSize: 11, marginLeft: "auto" }}>{actions.ai_mode}</span>
          </div>
          <div data-testid="actions-immediate" style={{ marginBottom: 12 }}>
            <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Immediate Actions</div>
            {(actions.immediate_actions || []).map((a, i) => (
              <div key={i} style={{ display: "flex", gap: 8, padding: "5px 0", color: "#e2e8f0", fontSize: 13 }}>
                <span style={{ color: "#f59e0b", fontWeight: 700 }}>{i + 1}.</span>{a}
              </div>
            ))}
          </div>
          {actions.recommended_workflow && (
            <div data-testid="actions-workflow" style={{ background: "#080c12", borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Workflow</div>
              <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.6 }}>{actions.recommended_workflow}</div>
            </div>
          )}
          {actions.str_sar_recommendation && (
            <div data-testid="actions-strsar" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.30)", borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ color: "#fca5a5", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>STR / SAR Recommendation</div>
              <div style={{ color: "#fecaca", fontSize: 13, lineHeight: 1.6 }}>{actions.str_sar_recommendation}</div>
            </div>
          )}
          {actions.senior_management_notes && (
            <div data-testid="actions-senior" style={{ background: "#080c12", borderRadius: 8, padding: 12 }}>
              <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Senior Management Notes</div>
              <div style={{ color: "#cbd5e1", fontSize: 13, lineHeight: 1.6 }}>{actions.senior_management_notes}</div>
            </div>
          )}
        </div>
      )}

      {/* History */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", marginTop: "24px" }}>
        <h2 style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 16px", color: "#f1f5f9" }}>Saved Articles ({articles.length})</h2>
        {articles.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: "13px", textAlign: "center", padding: "32px" }}>
            No articles saved yet. Paste your first article above to get started.
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "10px" }}>
            {articles.map((a) => {
              const ext = a.extraction || {};
              return (
                <div
                  key={a.id}
                  onClick={() => setSelectedArticle(a)}
                  data-testid={`article-card-${a.id}`}
                  style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "14px 18px", cursor: "pointer", display: "grid", gridTemplateColumns: "1fr auto auto auto", alignItems: "center", gap: "16px", transition: "all 0.2s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#2563eb"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#1e2530"; }}
                >
                  <div style={{ overflow: "hidden" }}>
                    <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600, marginBottom: "2px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {a.title || (a.article_text || "").slice(0, 80) + "..."}
                    </div>
                    <div style={{ color: "#64748b", fontSize: "11px" }}>
                      {RISK_CATEGORY_LABELS[ext.risk_category] || ext.risk_category} · {new Date(a.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div style={{ color: "#94a3b8", fontSize: "11px" }}>
                    {a.matched_customers?.length || 0} match{(a.matched_customers?.length || 0) === 1 ? "" : "es"}
                  </div>
                  <div style={{ color: a.created_cases?.length ? "#f59e0b" : "#475569", fontSize: "11px", fontWeight: 600 }}>
                    {a.created_cases?.length || 0} case{(a.created_cases?.length || 0) === 1 ? "" : "s"}
                  </div>
                  <SeverityBadge level={ext.severity_level} score={ext.severity_score} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ArticleDetailModal
        article={selectedArticle}
        onClose={() => setSelectedArticle(null)}
        onDelete={handleDelete}
      />
    </div>
  );
}
